import { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { loadGoogleMaps } from "../lib/googleMaps";
import { resolveUsState } from "../lib/usStates";
import { MapPin, CheckCircle, WarningCircle } from "@phosphor-icons/react";

const EMPTY = { line1: "", line2: "", city: "", state: "", postal_code: "", country: "US", formatted: "", verified: false };

function parsePlace(place) {
    const comp = {};
    (place.address_components || []).forEach((c) => {
        c.types.forEach((t) => { comp[t] = c; });
    });
    const g = (t, use = "long_name") => (comp[t] ? comp[t][use] : "");
    const line1 = [g("street_number"), g("route")].filter(Boolean).join(" ").trim();
    const postal = g("postal_code");
    return {
        line1,
        line2: "",
        city: g("locality") || g("sublocality") || g("postal_town") || g("administrative_area_level_2"),
        state: resolveUsState({
            shortName: g("administrative_area_level_1", "short_name"),
            longName: g("administrative_area_level_1", "long_name"),
            postalCode: postal,
        }),
        postal_code: postal,
        country: g("country", "short_name") || "US",
        formatted: place.formatted_address || "",
        verified: false, // will be flipped by optional server-side validation
    };
}

export default function AddressAutocomplete({
    label,
    value,
    onChange,
    required,
    testIdPrefix = "addr",
    hint,
    onValidate,
}) {
    const v = value || EMPTY;
    const line1Ref = useRef(null);
    const autocompleteRef = useRef(null);
    const [ready, setReady] = useState(false);
    const [validating, setValidating] = useState(false);
    const [manual, setManual] = useState(false);

    useEffect(() => {
        let cancelled = false;
        loadGoogleMaps()
            .then((g) => {
                if (cancelled) return;
                if (!g || !line1Ref.current) {
                    setManual(true);
                    return;
                }
                const ac = new g.maps.places.Autocomplete(line1Ref.current, {
                    types: ["address"],
                    componentRestrictions: { country: ["us"] },
                    fields: ["address_components", "formatted_address"],
                });
                autocompleteRef.current = ac;
                ac.addListener("place_changed", () => {
                    const place = ac.getPlace();
                    if (!place || !place.address_components) return;
                    const parsed = parsePlace(place);
                    onChange({ ...v, ...parsed });
                });
                setReady(true);
            })
            .catch(() => setManual(true));
        return () => { cancelled = true; };
    }, []);

    const set = (patch) => onChange({ ...v, ...patch, verified: false });

    // Auto-fill state from ZIP when the user types a new ZIP and state is empty.
    // This is the escape hatch when Google Places didn't return
    // administrative_area_level_1 for the picked address.
    const setZip = (zip) => {
        const patch = { postal_code: zip };
        if (!v.state && zip && zip.length >= 3) {
            const inferred = resolveUsState({ postalCode: zip });
            if (inferred) patch.state = inferred;
        }
        set(patch);
    };

    const runValidate = async () => {
        if (!onValidate) return;
        if (!v.line1 || !v.city || !v.state || !v.postal_code) return;
        setValidating(true);
        try {
            const result = await onValidate({
                address_lines: [v.line1, [v.city, v.state, v.postal_code].filter(Boolean).join(", ")],
                region_code: v.country || "US",
            });
            if (result) {
                onChange({
                    ...v,
                    formatted: result.formatted_address || v.formatted,
                    postal_code: result.postal_code || v.postal_code,
                    verified: result.verdict === "verified",
                });
            }
        } finally {
            setValidating(false);
        }
    };

    return (
        <div className="space-y-2" data-testid={`${testIdPrefix}-block`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label className="text-sm font-medium">
                    {label}{required ? <span className="text-rose-600 ml-0.5">*</span> : null}
                </Label>
                {v.verified ? (
                    <Badge variant="outline" className="gap-1 bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`${testIdPrefix}-verified`}>
                        <CheckCircle size={12} weight="bold" /> USPS verified
                    </Badge>
                ) : (v.line1 && v.city && v.state) ? (
                    <button
                        type="button"
                        onClick={runValidate}
                        disabled={validating || !onValidate}
                        data-testid={`${testIdPrefix}-verify-btn`}
                        className="text-xs text-slate-600 underline hover:text-[#0F1E3D] disabled:opacity-50"
                    >
                        {validating ? "Verifying…" : "Verify address"}
                    </button>
                ) : null}
            </div>
            <div className="relative">
                <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
                <Input
                    ref={line1Ref}
                    value={v.line1 || ""}
                    onChange={(e) => set({ line1: e.target.value })}
                    placeholder={ready ? "Start typing an address…" : "Street address"}
                    required={required}
                    autoComplete="off"
                    data-testid={`${testIdPrefix}-line1`}
                    className="pl-9"
                />
            </div>
            <Input
                value={v.line2 || ""}
                onChange={(e) => set({ line2: e.target.value })}
                placeholder="Apt / Suite / Unit (optional)"
                data-testid={`${testIdPrefix}-line2`}
            />
            <div className="grid grid-cols-6 gap-2">
                <Input
                    className="col-span-3"
                    value={v.city || ""}
                    onChange={(e) => set({ city: e.target.value })}
                    placeholder="City"
                    required={required}
                    data-testid={`${testIdPrefix}-city`}
                />
                <Input
                    className={`col-span-1 uppercase ${required && !v.state ? "border-rose-300 bg-rose-50" : ""}`}
                    maxLength={2}
                    value={v.state || ""}
                    onChange={(e) => set({ state: e.target.value.toUpperCase() })}
                    placeholder="ST"
                    required={required}
                    autoComplete="address-level1"
                    aria-label="State (2-letter abbreviation)"
                    data-testid={`${testIdPrefix}-state`}
                />
                <Input
                    className={`col-span-2 ${required && !v.postal_code ? "border-rose-300" : ""}`}
                    value={v.postal_code || ""}
                    onChange={(e) => setZip(e.target.value)}
                    placeholder="ZIP"
                    required={required}
                    inputMode="numeric"
                    autoComplete="postal-code"
                    data-testid={`${testIdPrefix}-zip`}
                />
            </div>
            {manual && !ready && (
                <p className="text-xs text-slate-500 flex items-center gap-1">
                    <WarningCircle size={12} /> Address suggestions unavailable — enter manually.
                </p>
            )}
            {hint && <p className="text-xs text-slate-500">{hint}</p>}
        </div>
    );
}
